import React, { useState, useEffect, useCallback, useRef } from 'react';
import { string, func, number } from 'prop-types';
import { noop } from 'lodash';
import classNames from 'classnames';
import { translate } from '@fiverr-private/i18n-react';
import { Input, Icon } from '@fiverr-private/fit';
import { r_search } from '@fiverr-private/icons';
import { reportSearchFilterEvent, TYPES } from '../../../../utils/reports/filter/searchFilter';
import withImpressions from '../../hoc/withImpressions';
import { CLASSNAME, IMPRESSION_CONFIG } from './constants';
import './styles.scss';

export const MAX_ALLOWED_LENGTH = 50;

// Quotes are whitelisted because they trigger a phrase search
export const BLOCKED_CHARS_REGEX = /[~@#$%^&*()_|+=;:<>{}[\]/]/;

/**
 * Search box component with text validations (min, max, space, alphabets only)
 * @param { object } props
 * @param { string } props.searchTerm - Controlled search term (used mostly upon "Clear all" button click)
 * @param { function } props.onSearch - Callback function to be called upon search
 * @param { number } props.maxChars - Specify the maximum charactes amount allowed to be inserted
 */
export const SearchBox = ({ searchTerm: initialSearchTerm = '', onSearch = noop, maxChars = MAX_ALLOWED_LENGTH }) => {
    const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
    const lastSearchedTermRef = useRef(initialSearchTerm);
    const wasFocusTrackedRef = useRef(false);

    // This hook keeps the sync of the search bar after external "Clear all" pressed
    useEffect(() => {
        if (searchTerm !== initialSearchTerm) {
            lastSearchedTermRef.current = initialSearchTerm;
            setSearchTerm(initialSearchTerm);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialSearchTerm]);

    const handleSearch = (ev) => {
        ev.preventDefault();
        const isValidLength = searchTerm.trim().length <= MAX_ALLOWED_LENGTH;

        if (isValidLength && searchTerm !== lastSearchedTermRef.current) {
            onSearch(searchTerm.trim());
            lastSearchedTermRef.current = searchTerm;
        }
    };

    const handleInputChange = useCallback((ev) => {
        const text = ev.target.value;

        if (!BLOCKED_CHARS_REGEX.test(text)) {
            setSearchTerm(text);
        }
    }, []);

    const handleFocus = useCallback(() => {
        if (!wasFocusTrackedRef.current) {
            wasFocusTrackedRef.current = true;
            reportSearchFilterEvent(TYPES.focus);
        }
    }, []);

    return (
        <form className={classNames(CLASSNAME, 'flex m-b-16')} onSubmit={handleSearch}>
            <Input
                className="search-input"
                type="search"
                id="searchTerm"
                icon={
                    <Icon color="white" size={16} onClick={handleSearch}>
                        {r_search}
                    </Icon>
                }
                iconClassName="search-icon"
                iconPosition={Input.ICON_POSITIONS.END}
                placeholder={translate('reviews.filters.search.search_placeholder')}
                value={searchTerm}
                maxLength={maxChars}
                onChange={handleInputChange}
                onFocus={handleFocus}
            />
        </form>
    );
};

SearchBox.propTypes = {
    searchTerm: string,
    onSearch: func,
    maxChars: number,
};

export default withImpressions(SearchBox, IMPRESSION_CONFIG);
